import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectInProgressRooms,
  selectInProgressLoaded,
  selectViewedRooms,
  setViewedRooms,
} from '../../redux/room/room.actions';
import { selectCurrentUser } from '../../redux/user/user.actions';
import { salasRecemAtribuidas } from '../../utils/salasRecemAtribuidas';
import PopSound from '../../assets/sounds/pop.wav';

/**
 * Item 8 (distribuição automática): quando um chat é atribuído automaticamente ao
 * operador (ele NÃO abriu manualmente), abre uma aba no QuickAccess já marcada como
 * "nova" (__isNew) — a aba fica piscando até o operador dar foco nela. Toca também um
 * som de aviso.
 *
 * Como detecta: observa as salas em progresso do operador (state.rooms.inProgressRooms,
 * que vêm de /rooms/{operatorId} e são atualizadas quando chega mensagem). Qualquer
 * sala nova que apareça e que ainda não esteja no QuickAccess é tratada como recém
 * atribuída.
 *
 * ## O cronômetro de 4 segundos saiu daqui (12/10/2026)
 *
 * A carência era de tempo: quatro segundos depois de montar, as salas carregadas
 * até ali viravam "já conhecidas". Mas este componente monta no `Template`, junto
 * com o app, e as salas em andamento só carregam quando o operador entra na tela
 * de chat — bem depois. Quando o cronômetro disparava, a lista de conhecidas
 * ficava VAZIA; quando as salas chegavam, **todas** contavam como recém-atribuídas,
 * entravam no QuickAccess e o som tocava.
 *
 * Era exatamente o que o dono do produto relatou: entrar na tela de chat abria
 * todas as salas disponíveis no QuickAccess, sem ter clicado em nenhuma.
 *
 * Agora o marco é **a primeira carga real da lista** (`inProgressLoaded`), e não o
 * relógio. A decisão em si mora em `utils/salasRecemAtribuidas`, com teste.
 */
const NewAssignedChatWatcher = () => {
  const dispatch = useDispatch();
  const inProgressRooms = useSelector(selectInProgressRooms);
  const inProgressLoaded = useSelector(selectInProgressLoaded);
  const viewedRooms = useSelector(selectViewedRooms);
  const currentUser = useSelector(selectCurrentUser);

  // room_ids já conhecidos (não devem piscar).
  const seenRef = useRef(new Set());
  // Se a primeira carga já foi absorvida por este componente.
  const primeiraCargaAbsorvidaRef = useRef(false);
  const viewedRef = useRef([]);
  const audioRef = useRef(null);

  viewedRef.current = Array.isArray(viewedRooms) ? viewedRooms : [];

  useEffect(() => {
    try {
      audioRef.current = new Audio(PopSound);
      audioRef.current.volume = 0.5;
    } catch {
      audioRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Sem carga ainda, não há o que decidir: é justamente a espera que o
    // cronômetro antigo não sabia fazer.
    if (!inProgressLoaded || !currentUser) return;

    const { novas, aConhecer } = salasRecemAtribuidas({
      emAndamento: inProgressRooms,
      jaConhecidas: seenRef.current,
      noQuickAccess: viewedRef.current,
      primeiraCargaJaOcorreu: primeiraCargaAbsorvidaRef.current,
    });

    // Toda sala avaliada passa a ser conhecida, inclusive as que não piscam --
    // senão seria reavaliada a cada atualização da lista.
    aConhecer.forEach((id) => seenRef.current.add(id));
    primeiraCargaAbsorvidaRef.current = true;

    if (novas.length === 0) return;

    dispatch(setViewedRooms([...viewedRef.current, ...novas]));

    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } catch {
        // ignore
      }
    }
  }, [inProgressRooms, inProgressLoaded, currentUser, dispatch]);

  return null;
};

export default NewAssignedChatWatcher;
